import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface SingleBlock {
    fields: {
        image: string,
        title: string,
        paragraph: string,
        linkText: string,
        link: LinkFieldValue;
    }
}

const enum fields {
    image = "image",
    title = "title",
    paragraph = "paragraph",
    linkText = "linkText",
    link = "link",
    blocks = "blocks"
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.blocks]: SingleBlock[];
    };
    extendedStyles?: BannerStyles;
}

export interface BannerStyles {
    overlayWrapperStyles?: InjectableCss;
    titleStyles?: InjectableCss;
}

const mapStateToProps = (state: ApplicationState) => ({
    pageTitle: getCurrentPage(state).fields.title,
});

type Props = OwnProps & WidgetProps & ReturnType<typeof mapStateToProps>;
export interface BlocksStyles {
    container?: GridContainerProps;
    customflyImgWrapper?: InjectableCss;
    customDescriptionText?: TypographyPresentationProps;
    customFlyTitle?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    rowWrapper?: InjectableCss;
    linkStyle?: LinkPresentationProps;
    title?: TypographyPresentationProps;
    imageContainer?: GridContainerProps;
    imageGrid?: GridItemProps;
    titleGrid?: GridItemProps;
    leftGrid?: GridItemProps;
    rightGrid?: GridItemProps;
    description?: TypographyPresentationProps;
    linkGrid?: GridItemProps;
}

export const blocksStyles: BlocksStyles = {
    wrapper: {
        css: css`
            padding: 70px 50px;
            .inner-blocks:nth-child(even) {
                background-color: #FAFAFB;
            }
        `
    },
    rowWrapper: {
        css: css`
            position: relative;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
        `
    },
    leftGrid: {
        width: [12, 12, 12, 4, 4]
    },
    rightGrid: {
        width: [12, 12, 12, 8, 8]
    },
    imageGrid: {
        width: 12,
        css: css`
            max-width: 100px;
            max-height: 100px;
        `
    },
    titleGrid: {
        width: 12,
        css: css`
            justify-content: center;
        `
    },
    imageContainer: {
        gap: 0,
        css: css`
            margin: 30px;
            > [class^="GridOffset-"] {
                justify-content: center;
                align-items: center;
            }
        `
    },
    title: {
        variant: "h4",
        css: css`
            font-family: Barlow-Bold;
            text-transform: uppercase;
            font-size: 24px;
            text-align: center;
            font-weight: 600;
            padding: 0 14%;
        `
    },
    description: {
        css: css`
            font-family: SourceSans3-Regular;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 30px;
                        `,
                        css`
                            padding: 30px;
                        `,
                        css`
                            padding: 30px;
                        `,
                        css`
                            padding-right: 30px;
                        `,
                        css`
                            padding-right: 30px;
                        `,
                    ],
                    "max",
                )}
        `
    },
    linkStyle: {
        css: css`
            font-weight: 600;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-bottom: 20px;
                        `,
                        css`
                            margin-bottom: 20px;
                        `,
                        css`
                            margin-bottom: 20px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `
    },
    linkGrid: {
        css: css`
            padding: 0px 15px 15px 15px;
        `
    }
}

const styles = blocksStyles;
const CustomBlocks: React.FunctionComponent<Props> = ({ fields }: Props) => {

    return (
        <StyledWrapper className="blocks-wrapper" {...styles.wrapper}>
            {fields.blocks.map((block: any, i: number) => {
                const link = useGetLink(block.fields.link);
                return (
                        <GridContainer key={i} className="inner-blocks">
                            <GridItem {...styles.leftGrid}>
                                <GridContainer {...styles.imageContainer}>
                                    <GridItem {...styles.imageGrid}>
                                        <LazyImage
                                            src={block.fields.image}
                                            altText=""
                                            title=""
                                        />
                                    </GridItem>
                                    <GridItem {...styles.titleGrid}>
                                        <Typography as="p" {...styles.title}>
                                            {translate(block.fields.title)}
                                        </Typography>
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                            <GridItem {...styles.rightGrid}>
                                <StyledWrapper {...styles.rowWrapper}>
                                    <GridContainer>
                                        <GridItem width={12} >
                                            <Typography {...styles.description}>
                                                {parse(block.fields.paragraph, parserOptions)}
                                            </Typography>
                                        </GridItem>
                                        { link.url && 
                                            <GridItem width={12} {...styles.linkGrid}>
                                                <StyledWrapper><Link href={link.url} target="_blank" {...styles.linkStyle}>
                                                    {translate(block.fields.linkText)}
                                                </Link></StyledWrapper>
                                            </GridItem>
                                        }
                                    </GridContainer>
                                </StyledWrapper>
                            </GridItem>
                        </GridContainer>
                )
            })}
            
        </StyledWrapper>
    )
}
const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CustomBlocks),
    definition: {
        group: "Basic",
        displayName: "Block List",
        fieldDefinitions: [
            {
                name: fields.blocks,
                editorTemplate: "ListField",
                tab: contentTab,
                defaultValue: [],
                getDisplay: ({ fields: { slideTitle } }) => slideTitle || "Slide",
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "paragraph",
                        editorTemplate: "RichTextField",
                        displayName: "",
                        defaultValue: "",
                        extendedConfig: { height: 100 },
                    },
                    {
                        name: "linkText",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "link",
                        displayName: "Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                ]
            }
        ],
    },
};

export default widgetModule;